/**
 * User Data Item Types (Provenance Tracking) Generic wrapper for any user data with source tracking.
 * Enables tracking where profile data originated.
 */

import { z } from "zod";
import type { JsonValueType } from "../types/json-value.types.js";

export const UserDataSource = {
  USER_PROVIDED: "user_provided",
  ENRICHED: "enriched",
  HOST_PROVIDED: "host_provided",
  IMPORTED: "imported",
  SYSTEM_DERIVED: "system_derived",
} as const;

export const UserDataSourceSchema = z.enum(UserDataSource);

export type UserDataSource = z.infer<typeof UserDataSourceSchema>;

export interface UserDataItem<T> {
  readonly value: T;
  readonly source: UserDataSource;
  readonly updatedAt: Date;
}

export function createUserDataItem<T>(
  value: T,
  source: UserDataSource,
  updatedAt: Date = new Date(),
): UserDataItem<T> {
  return { value, source, updatedAt };
}

export interface UserDataCustomField<T = JsonValueType>
  extends UserDataItem<T> {
  readonly key: string;
  readonly label: string | null;
}
// `z.coerce.date()`-based user-data-item schema: accepts an ISO string (or
// Date) on parse and yields a `Date`. Used by the schema-based entity schemas.
export function userDataItemSchema<S extends z.ZodType>(valueSchema: S) {
  return z.object({
    value: valueSchema,
    source: UserDataSourceSchema,
    updatedAt: z.coerce.date(),
  });
}
